import React, { Suspense, lazy, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from 'styled-components';
import {
  createResetActiveItemAction,
  createSetActiveItemAction,
} from '../../actions/header-actions';
import { DealerContext } from '../../context';
import type { AudiHeaderProps } from '../../interfaces/header-components.interfaces';
import { useClickOnElementByClassName, useStateSwitchTimeOutHook } from '../../services/hooks';
import AudiHeaderLogo from '../AudiHeaderLogo/AudiHeaderLogo';
import AudiHeaderMenuButton from '../AudiHeaderMenuButton/AudiHeaderMenuButton';
import AudiHeaderMiniCart from '../AudiHeaderMiniCart/AudiHeaderMiniCart';
import AudiHeaderNav from '../AudiHeaderNav/AudiHeaderNav';
import AudiHeaderSearchButton from '../AudiHeaderSearchButton/AudiHeaderSearchButton';
import AudiHeaderSearchButtonAlpha from '../AudiHeaderSearchButton/AudiHeaderSearchButton_alpha';
import AudiSearchFeatureAppLoader from '../AudiHeaderSearch/AudiSearchFeatureAppLoader';
import AudiUserMenu from '../AudiUserMenu/AudiUserMenu';
import AudiWishlist from '../AudiWishlist/AudiWishlist';
import { timeS } from './AudiHeaderStyleUtils';
import {
  HeaderStyled,
  HeaderStyledShader,
  HeaderStyledUserActionWrapper,
  HeaderStyledWrapper,
} from './AudiHeaderStyles';
import { mobileMenuClick, EventAction } from '../../utils/tracking';
import type { TrackingData } from '../../feature-service-types';
import { ServiceContext } from '../../context/ServiceContext';
import { ConfigContext } from '../../context/ConfigContext';
import { isDealerDomain } from '../../utils/is-dealer-domain';
import { getKeyboardFocusableElements, keyTypes, navigate, trapFocus } from '../../utils/a11y';
import useKeyboard from '../../hooks/use-keyboars-events';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import DealerComponents from './AudiDealerComponents';
import { calculateWidthWithMargins } from '../../utils/nav-utils';

const SearchFeatureApp = lazy(
  () => import(/* webpackChunkName: "lazy-search" */ '../AudiHeaderSearch/AudiSearchFeatureApp'),
);

const AiSearchFeatureApp = lazy(
  () => import(/* webpackChunkName: "lazy-ai-search" */ '../AudiHeaderSearch/AiSearchFeatureApp'),
);

const AudiHeader: React.FC<AudiHeaderProps> = (props) => {
  const { headerStateService, layerManager, trackingService, dealerContextService } =
    useContext(ServiceContext);
  const {
    featureAppId,
    loginFeatureAppUrl,
    searchInputFeatureAppUrl,
    searchResultsFeatureAppUrl,
    miniCartFeatureAppBaseUrl,
    miniCartFeatureAppSrc,
    oneShopUbffUrl,
    tierOneUrl,
    dealerSearch,
    dealerSearchGeolocationEnabled,
    useAiSearch,
  } = useContext(ConfigContext);
  const {
    headerState,
    headerDispatch,
    actionWrapperElement,
    headerConfig,
    isLoading,
    wrapperElement,
    navigationWrapperElement,
    logoWrapperElement,
    useOneLayer,
    useFootnoteReferenceServiceTextParserHook,
    useUserMenu,
    isOneCms,
  } = props;
  const headerNavigationItemsAmount = useMemo(
    () => headerConfig?.MainNavigation?.length || 0,
    [headerConfig?.MainNavigation?.length],
  );

  const { dealerData, renderDealerContext } = useContext(DealerContext);

  const isTierOneDomain = isDealerDomain(tierOneUrl);
  const showDealerContext = !!dealerData && renderDealerContext && isTierOneDomain;

  const showSearchButton =
    headerConfig?.Search && searchInputFeatureAppUrl && searchResultsFeatureAppUrl;
  const showMiniCart =
    !dealerData &&
    headerConfig?.OneShopEnabled &&
    miniCartFeatureAppBaseUrl &&
    miniCartFeatureAppSrc &&
    oneShopUbffUrl;
  const showMyAudi = headerConfig?.Login && loginFeatureAppUrl;
  const actionItems = [showDealerContext, showSearchButton, showMiniCart, showMyAudi, showMyAudi];
  const totalActionItems = useMemo(() => actionItems.filter(Boolean).length, [actionItems]);
  const showDealerShader = process.env.IS_LEGACY ? headerState.activeItem.showDealer : false;
  const themeContext = useContext(ThemeContext);

  const showShader = useMemo(
    () =>
      headerState.activeItem.index !== -1 || headerState.activeItem.showSearch || showDealerShader,
    [headerState.activeItem],
  );

  const [showShaderDelayed, setShowShaderDelayed] = useState(showShader);
  const [isElementsOverflow, setIsElementsOverflow] = useState(false);

  useEffect(() => {
    setTimeout(
      () => {
        setShowShaderDelayed(showShader);
      },
      showShader ? 1 : parseInt(timeS, 10),
    );
  }, [showShader]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [disableTransition, setDisableTransition] = useState<boolean>(false);

  const { state: isClosing, stateSwitch: addClosingAnimation } = useStateSwitchTimeOutHook(440);

  const [windowWidth, setWindowWith] = useState(
    (typeof window !== 'undefined' && window.innerWidth) || 0,
  );

  // Learn more link for User Menu
  const marketUrl = headerConfig?.Login?.MarketUrl;

  // optional "Overview" page on CTA "Login"
  const idPageUrl = headerConfig?.Login?.OverviewUrl;

  // myAudi Account link
  const homeUrl = `${headerConfig?.Login?.MyAudiAccountUrl}`;

  // myAudi Wishlist link
  const wishlistUrl = headerConfig?.Login?.WishlistUrl;

  // logout link
  const redirectUrl = headerConfig?.Login?.RedirectUrl;

  useEffect(() => {
    let isMounted = true;
    const resizeHandler = (): void => {
      let timer: ReturnType<typeof setTimeout> | null;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (timer) {
        clearTimeout(timer);
        timer = null;
      } else {
        setDisableTransition(true);
      }

      timer = setTimeout(() => {
        if (isMounted) {
          setDisableTransition(false);
        }
        timer = null;
      }, 200);
    };
    resizeHandler();

    window.addEventListener('resize', resizeHandler);

    return (): void => {
      isMounted = false;
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  useEffect(() => {
    const resizeHandler = (): void => {
      const windowWidth = (typeof window !== 'undefined' && window.innerWidth) || 0;
      const result = calculateWidthWithMargins(
        themeContext,
        windowWidth,
        headerNavigationItemsAmount,
        dealerSearch,
        showDealerContext,
      );
      setWindowWith(windowWidth);
      setIsElementsOverflow(result.isElementsOverflow);
    };
    resizeHandler();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', resizeHandler);
    }
    return (): void => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', resizeHandler);
      }
    };
  }, [dealerSearch, showDealerContext]);

  const closeMenu: () => void = () => {
    const layers = layerManager?.getLayers() || [];
    if (layers.length > 0) {
      return;
    }

    // Fire tracking events only when AI search is active
    if (useAiSearch && headerState.activeItem.showSearch) {
      // Event 1: internal_link
      const closeLayerEvent: TrackingData = {
        event: {
          eventInfo: {
            eventAction: EventAction.INTERNAL,
            eventName: 'one header - click to close layer',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: '',
            targetURL: '',
            clickID: '',
            elementName: 'icon',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(closeLayerEvent);

      // Event 2: view_change
      const viewChangeEvent: TrackingData = {
        event: {
          eventInfo: {
            eventAction: 'view_change',
            eventName: 'generic',
          },
          attributes: {
            componentName: 'one-header',
            label: '',
            targetURL: '',
            clickID: '',
            elementName: '',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(viewChangeEvent);
    }

    headerDispatch(createResetActiveItemAction(headerStateService));
    addClosingAnimation();

    setIsOpen(false);
  };

  const closeMenuOnNavigationLinkClick = () => {
    setIsOpen(false);
  };

  useClickOnElementByClassName({
    className: 'audi-j-footnote-reference',
    clickCallBack: closeMenu,
  });

  useEffect(() => {
    const expandedClass = 'u-header-expanded';
    if (isOpen || headerState.activeItem.showSearch) {
      document.body.classList.add(expandedClass);
    } else {
      document.body.classList.remove(expandedClass);
    }
  }, [isOpen, headerState.activeItem.showSearch]);

  useEffect(() => {
    let isMounted = true;

    if (isLoading) {
      setDisableTransition(true);
    } else {
      setTimeout(() => {
        if (isMounted) {
          setDisableTransition(false);
        }
      }, 660);
    }

    return (): void => {
      isMounted = false;
    };
  }, [isLoading]);

  useEffect(() => {
    headerStateService.registerCallback((headerStateRemote) => {
      if (headerStateRemote.showLoginFlyout === true) {
        setIsOpen(false);
      }
    });
  }, [headerStateService, headerDispatch]);

  const onClickMenuButton: () => void = () => {
    if (!isOpen) {
      setIsOpen(!isOpen);
      headerDispatch(createResetActiveItemAction(headerStateService));
      headerStateService.setShowLoginFlyout(false);
      trackingService?.track(mobileMenuClick('open', headerState.activeItem.index, 'text link'));
    } else {
      closeMenu();
      trackingService?.track(mobileMenuClick('close', headerState.activeItem.index, 'icon'));
    }
  };

  const handleClickWishlist: () => void = () => {
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: true,
        openDealerDetails: false,
      }),
    );
  };

  const onClickSearch: () => void = () => {
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: true,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
  };

  const onClickDealerContext: () => void = () => {
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: !headerState.activeItem.showDealer,
        showDealerSearch: headerState.activeItem.showDealerSearch,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
  };

  const onClickDealerSearchContext: () => void = () => {
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: true,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
    // remove fa-header-dealer-data from session storage
    dealerContextService.setSelectedDealer(undefined);
  };

  const isSmallView = windowWidth <= themeContext?.breakpoints?.l - 1 || isElementsOverflow;
  const legacyClassNames = isSmallView
    ? 'header header--loaded header--view-small'
    : 'header header--loaded';

  useEffect(() => {
    headerStateService.setisMobileView(isSmallView);
    if (!isSmallView) {
      setIsOpen(false);
    }
  }, [isSmallView, headerStateService]);

  // Listen for custom event to open mobile menu
  useEffect(() => {
    const handleOpenMobileMenu = (): void => {
      if (isSmallView && !isOpen) {
        setIsOpen(true);
        headerDispatch(createResetActiveItemAction(headerStateService));
      }
    };

    window.addEventListener('openMobileMenu', handleOpenMobileMenu);

    return (): void => {
      window.removeEventListener('openMobileMenu', handleOpenMobileMenu);
    };
  }, [isSmallView, isOpen, headerDispatch, headerStateService]);

  // Listen for dealer connection/disconnection events to close mobile menu
  useEffect(() => {
    const handleDealerDataUpdated = (): void => {
      if (isSmallView && isOpen) {
        closeMenu();
      }
    };

    window.addEventListener('dealerDataUpdated', handleDealerDataUpdated);

    return (): void => {
      window.removeEventListener('dealerDataUpdated', handleDealerDataUpdated);
    };
  }, [isSmallView, isOpen]);

  useKeyboard(
    'keydown',
    (event) => {
      const wrapElement = wrapperElement?.current as HTMLDivElement;
      if (!wrapElement) return;

      switch (event.key) {
        // close menus
        case keyTypes.Escape:
          closeMenu();
          break;
        // trigger left and right keyboard navigation
        case keyTypes.ArrowRight:
        case keyTypes.ArrowLeft:
          if (!isSmallView) {
            navigate(
              wrapElement,
              event.key,
              keyTypes.ArrowRight,
              keyTypes.ArrowLeft,
              `a.${NAVIGATION_CLASSNAME}, button.${NAVIGATION_CLASSNAME}, .${NAVIGATION_CLASSNAME} a, .${NAVIGATION_CLASSNAME} button`,
            );
          }
          break;
        // trigger up and down keyboard navigation on small screens
        case keyTypes.ArrowDown:
        case keyTypes.Tab:
          if (isSmallView) {
            const focusedElements = getKeyboardFocusableElements(
              wrapElement,
              true,
              NAVIGATION_MOBILE_CLASSNAME,
            );
            trapFocus(focusedElements, event);

            navigate(
              wrapElement,
              event.key,
              keyTypes.ArrowDown,
              keyTypes.ArrowUp,
              `.${NAVIGATION_MOBILE_CLASSNAME}`,
            );
          }
          break;
      }
    },
    wrapperElement,
  );

  if (!themeContext) {
    return;
  }

  return (
    <HeaderStyled
      className={legacyClassNames}
      data-fefa-custom-id={featureAppId}
      data-module="one-header"
      data-navigation-items-amount={headerNavigationItemsAmount}
      disableTransition={disableTransition}
      ref={wrapperElement}
    >
      {showShader
        ? showShader && (
            <HeaderStyledShader
              onClick={closeMenu}
              showShader={showShaderDelayed}
              useAiSearch={useAiSearch}
            />
          )
        : showShaderDelayed && (
            <HeaderStyledShader
              onClick={closeMenu}
              showShader={showShader}
              useAiSearch={useAiSearch}
            />
          )}
      <HeaderStyledWrapper headerNavigationItemsAmount={headerNavigationItemsAmount}>
        <AudiHeaderLogo
          theme={themeContext}
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          isLoading={isLoading}
          isOpen={isOpen}
          logoLink={headerConfig?.Logo}
          logoWrapperElement={logoWrapperElement}
        />
        {Boolean(headerNavigationItemsAmount) && (
          <AudiHeaderMenuButton
            headerNavigationItemsAmount={headerNavigationItemsAmount}
            headerDispatch={headerDispatch}
            isLoading={isLoading}
            isOpen={isOpen}
            label={headerConfig?.MenuLabel}
            onClick={onClickMenuButton}
            onClickSearch={showSearchButton ? onClickSearch : undefined}
          />
        )}
        <>
          <AudiHeaderNav
            navigationWrapperElement={navigationWrapperElement}
            headerDispatch={headerDispatch}
            headerNavigationItemsAmount={headerNavigationItemsAmount}
            headerState={headerState}
            isClosing={isClosing}
            isOpen={isOpen}
            onNavigationLinkClick={closeMenuOnNavigationLinkClick}
            navigation={headerConfig?.MainNavigation}
            useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
            useOneLayer={useOneLayer}
            windowWidth={windowWidth}
            onClickDealerContext={onClickDealerContext}
            onClickDealerSearchContext={onClickDealerSearchContext}
            showDealerAdditionalData={headerState.activeItem.showDealer}
            dealerSearch={dealerSearch}
            dealerSearchGeolocationEnabled={dealerSearchGeolocationEnabled}
            isTierOneDomain={isTierOneDomain}
          />
          <HeaderStyledUserActionWrapper
            isLoading={isLoading}
            isSmallView={isSmallView}
            ref={actionWrapperElement}
          >
            {renderDealerContext && (
              <DealerComponents
                headerState={headerState}
                showDealerContext={showDealerContext}
                headerNavigationItemsAmount={headerNavigationItemsAmount}
                dealerSearch={dealerSearch}
                dealerSearchGeolocationEnabled={dealerSearchGeolocationEnabled}
                isTierOneDomain={isTierOneDomain}
                onClickDealerContext={onClickDealerContext}
                onClickDealerSearchContext={onClickDealerSearchContext}
                isSmallView={isSmallView}
              />
            )}

            {process.env.IS_LEGACY
              ? showSearchButton && (
                  <AudiHeaderSearchButton
                    headerDispatch={headerDispatch}
                    headerNavigationItemsAmount={headerNavigationItemsAmount}
                    isOpen={isOpen}
                    search={headerConfig.Search}
                  />
                )
              : showSearchButton && (
                  <AudiHeaderSearchButtonAlpha
                    headerDispatch={headerDispatch}
                    headerNavigationItemsAmount={headerNavigationItemsAmount}
                    isOpen={isOpen}
                    search={headerConfig.Search}
                  />
                )}
            {showMiniCart && (
              <AudiHeaderMiniCart
                headerNavigationItemsAmount={
                  headerNavigationItemsAmount || headerNavigationItemsAmount + 1
                }
                isOpen={isOpen}
              />
            )}
            {showMyAudi && (
              <>
                <AudiWishlist
                  headerDispatch={headerDispatch}
                  headerState={headerState}
                  headerNavigationItemsAmount={headerNavigationItemsAmount}
                  isOpen={isOpen}
                  useUserMenu={useUserMenu}
                  wishlistUrl={wishlistUrl as string}
                  onClickWishlist={handleClickWishlist}
                />
                <AudiUserMenu
                  isOneCms={isOneCms}
                  headerDispatch={headerDispatch}
                  headerState={headerState}
                  headerNavigationItemsAmount={headerNavigationItemsAmount}
                  isOpen={isOpen}
                  myAudiUrl={headerConfig?.Login?.Url}
                  homeUrl={homeUrl}
                  idPageUrl={idPageUrl}
                  marketBaseUrl={marketUrl}
                  useUserMenu={useUserMenu}
                  redirectUrl={redirectUrl}
                  supplier={headerConfig?.Suppliers}
                  totalActionItems={totalActionItems}
                />
              </>
            )}
          </HeaderStyledUserActionWrapper>
          {headerState.activeItem.showSearch &&
            (useAiSearch ? (
              <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="search" />}>
                <AiSearchFeatureApp
                  headerDispatch={headerDispatch}
                  showSearch={headerState.activeItem.showSearch}
                />
              </Suspense>
            ) : (
              headerConfig?.Search &&
              searchInputFeatureAppUrl &&
              searchResultsFeatureAppUrl && (
                <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="search" />}>
                  <SearchFeatureApp
                    dealerSearchLink={headerConfig.Search.DealerSearchLink}
                    oneHeaderSearchClientId={headerConfig.Search.OneHeaderSearchClientId}
                    vtpConfig={{
                      dealerSearch: !!headerConfig.Search.ShowDealerSearchLink,
                      newStockCarSearch: !!headerConfig.Search.ShowNewCarsLink,
                      usedAndPreOwnedStockCarSearch: !!headerConfig.Search.ShowUsedCarsLink,
                    }}
                    mainNavigation={headerConfig.MainNavigation}
                    headerConfig={headerConfig}
                    headerDispatch={headerDispatch}
                    legend={headerConfig.Search.Legend}
                    showSearch={headerState.activeItem.showSearch}
                  />
                </Suspense>
              )
            ))}
        </>
      </HeaderStyledWrapper>
    </HeaderStyled>
  );
};

export default AudiHeader;
